import React, { useState, useEffect, useRef, useCallback } from 'react';

function FullScreenPhotoViewer({ photos = [], initialIndex = 0, onClose, show = false }) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    
    const imageRef = useRef(null);
    const containerRef = useRef(null);
    const isPanningRef = useRef(false);
    const hasDraggedRef = useRef(false);
    const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
    }, [photos.length]);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
    }, [photos.length]);

    useEffect(() => {
        setCurrentIndex(initialIndex);
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
    }, [initialIndex]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!show) return;

            switch (e.key) {
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
                case 'Escape':
                    onClose();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [show, goToNext, goToPrevious, onClose]);

    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [show]);

    const handleClickOutside = useCallback((e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    }, [onClose]);
    
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!containerRef.current || !imageRef.current) return;
        
        const rect = containerRef.current.getBoundingClientRect();
        const rectImg = imageRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const oldZoom = zoomLevel;
        const newZoom = Math.min(Math.max(1, oldZoom + delta), 4);
        
        if (newZoom === 1) {
            setZoomLevel(1);
            setPanPosition({ x: 0, y: 0 });
            return;
        }
        
        // Calculate the mouse position relative to the image's current position
        const imgWidth = rectImg.width / oldZoom;
        const imgHeight = rectImg.height / oldZoom;
        
        // Calculate the image's current origin (top-left corner)
        const imgOriginX = centerX - imgWidth / 2 + panPosition.x;
        const imgOriginY = centerY - imgHeight / 2 + panPosition.y;
        
        // Calculate mouse position relative to the image origin
        const mouseInImageX = mouseX - imgOriginX;
        const mouseInImageY = mouseY - imgOriginY;
        
        // Calculate the new image origin to keep the mouse at the same relative position
        const newImgOriginX = mouseX - mouseInImageX * (newZoom / oldZoom);
        const newImgOriginY = mouseY - mouseInImageY * (newZoom / oldZoom);
        
        // Calculate the new pan position
        const newPanX = newImgOriginX - centerX + imgWidth / 2;
        const newPanY = newImgOriginY - centerY + imgHeight / 2;
        
        setZoomLevel(newZoom);
        setPanPosition({ x: newPanX, y: newPanY });
    }, [zoomLevel, panPosition]);
    
    const handleDoubleClick = useCallback((e) => {
        if (hasDraggedRef.current) {
            hasDraggedRef.current = false;
            return;
        }
        
        if (zoomLevel > 1) {
            setZoomLevel(1);
            setPanPosition({ x: 0, y: 0 });
        } else {
            if (!containerRef.current || !imageRef.current) return;
            
            const rect = containerRef.current.getBoundingClientRect();
            const rectImg = imageRef.current.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            
            const oldZoom = zoomLevel;
            const newZoom = 2;
            
            // Calculate the mouse position relative to the image's current position
            const imgWidth = rectImg.width / oldZoom;
            const imgHeight = rectImg.height / oldZoom;
            
            // Calculate the image's current origin (top-left corner)
            const imgOriginX = centerX - imgWidth / 2 + panPosition.x;
            const imgOriginY = centerY - imgHeight / 2 + panPosition.y;
            
            // Calculate mouse position relative to the image origin
            const mouseInImageX = mouseX - imgOriginX;
            const mouseInImageY = mouseY - imgOriginY;
            
            // Calculate the new image origin to keep the mouse at the same relative position
            const newImgOriginX = mouseX - mouseInImageX * (newZoom / oldZoom);
            const newImgOriginY = mouseY - mouseInImageY * (newZoom / oldZoom);
            
            // Calculate the new pan position
            const newPanX = newImgOriginX - centerX + imgWidth / 2;
            const newPanY = newImgOriginY - centerY + imgHeight / 2;
            
            setZoomLevel(newZoom);
            setPanPosition({ x: newPanX, y: newPanY });
        }
    }, [zoomLevel, panPosition]);
    
    const handleMouseDown = useCallback((e) => {
        hasDraggedRef.current = false;
        if (zoomLevel > 1) {
            isPanningRef.current = true;
            panStartRef.current = {
                x: e.clientX,
                y: e.clientY,
                panX: panPosition.x,
                panY: panPosition.y
            };
        }
    }, [zoomLevel, panPosition]);
    
    const handleMouseMove = useCallback((e) => {
        if (!isPanningRef.current) return;
        
        const dx = e.clientX - panStartRef.current.x;
        const dy = e.clientY - panStartRef.current.y;
        
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
            hasDraggedRef.current = true;
        }
        
        setPanPosition({
            x: panStartRef.current.panX + dx,
            y: panStartRef.current.panY + dy
        });
    }, []);
    
    const handleMouseUp = useCallback(() => {
        isPanningRef.current = false;
    }, []);
    
    const handleMouseLeave = useCallback(() => {
        isPanningRef.current = false;
    }, []);
    
    if (!show || photos.length === 0) {
        return null;
    }
    
    const safeIndex = Math.min(Math.max(0, currentIndex), photos.length - 1);
    const currentPhoto = photos[safeIndex];
    const isZoomed = zoomLevel > 1;
    
    return (
        <div
            className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4"
            onClick={handleClickOutside}
        >
            <div className="relative w-full h-full flex items-center justify-center select-none" style={{ touchAction: 'none' }}>
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all"
                    aria-label="Close full-screen viewer"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
                
                <div ref={containerRef} className="relative w-full h-full flex items-center justify-center overflow-hidden">
                    <img
                        ref={imageRef}
                        src={currentPhoto.media_url}
                        alt={`Slide ${safeIndex + 1} of ${photos.length}`}
                        className="max-w-full max-h-full object-contain cursor-pointer"
                        style={{
                            transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                            transformOrigin: '0 0',
                            transition: isPanningRef.current ? 'none' : 'transform 0.1s ease-out',
                            cursor: isZoomed ? 'grab' : 'pointer',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                        }}
                        onClick={handleDoubleClick}
                        onWheel={handleWheel}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        draggable={false}
                    />
                </div>
                
                {photos.length > 1 && (
                    <>
                        <button
                            onClick={goToPrevious}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all"
                            aria-label="Previous photo"
                        >
                            <svg
                                className="w-8 h-8"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                        
                        <button
                            onClick={goToNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all"
                            aria-label="Next photo"
                        >
                            <svg
                                className="w-8 h-8"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </button>
                    </>
                )}
                
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                    {safeIndex + 1} / {photos.length}
                </div>
            </div>
        </div>
    );
}

export default FullScreenPhotoViewer;